import ApartmentIcon from "@mui/icons-material/Apartment";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { MenuItem, Stack, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import StatusBlockDialog from "../../../components/Dialogs/StatusBlockDialog";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../components/TextInputs";
import CustomSelect from "../../../components/customSelect";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { getAllCompanies } from "../../../services/comanyApiFn";
import { updateAllAcountStatus } from "../../../services/commonApiFn";
import { getAllWorkers } from "../../../services/workerApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = [
  "createdAt",
  "workerDetails",
  "companyDetails",
  "status",
  // "Actions",
];

const tableRows = [
  "created_date",
  "worker_details",
  "company_worker_detail",
  "status",
  // "actions",
];

function WokerLists() {
  const { t } = useTranslation(["client", "common"]);
  const openBlockStatusDialog = useRef(null);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allWorkers, setAllWorkers] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.workerByName, 1000);

  useEffect(() => {
    fetchAllWorkers(debouncedSearchTerm, searchKeys.workerByCompanyId);
  }, [debouncedSearchTerm, searchKeys.workerByCompanyId]);

  useEffect(() => {
    fetchAllCompanies();
  }, []);

  const fetchAllWorkers = async (name, companyId) => {
    try {
      setIsLoading(true);
      const response = await getAllWorkers(name, companyId);
      if (response.data.status === "success") {
        const data = response.data.data.workers;
        setAllWorkers(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllCompanies = async (search) => {
    try {
      setIsLoading(true);
      const params = {
        search: search,
        page: 1,
        limit: 500
      }
      const response = await getAllCompanies(params);
      if (response.data.status === "success") {
        const data = response.data.data.companies;
        setAllCompanies(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenConfirmDialog = (e, row) => {
    openBlockStatusDialog.current.openDialog({
      type: "Worker",
      status: e ? "Unblock" : "Block",
      data: row,
      event: e,
    });
  };

  const handleUpdateUserStatus = async (params) => {
    try {
      let payload = {
        status: params?.event,
        reason: params.reason,
      };
      setIsLoading(true);
      const response = await updateAllAcountStatus(
        params?.data?._id,
        payload,
        "Worker"
      );
      if (response.data.status === "success") {
        fetchAllWorkers(searchKeys.workerByName, searchKeys.workerByCompanyId);
        handleOpenSnackBar("success", response.data.message);
        openBlockStatusDialog.current.closeDialog({});
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareExportData = () => {
    return allWorkers?.map((item) => ({
      "Created Date": moment(item.createdAt).format("MM-DD-YYYY"),
      "Worker Name": item.name,
      "Worker Email": item.email,
      "Worker Contact": item.contact,
      "Worker Location": item.location.addressLabel,
      "Company Name": item.companyId.name,
      "Company Email": item.companyId.email,
      "Company Contact": item.companyId.contact,
      "Company Location": item.companyId.location.addressLabel,
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack flexGrow={1} direction={{ md: "row" }} justifyContent={"flex-end"}>
        <CSVLink
          data={prepareExportData()}
          filename={"workers_list.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack
        p={2}
        gap={1}
        direction={{ md: "row" }}
        bgcolor={"primary.light"}
        alignItems={"center"}
      >
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.workerByName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      workerByName: "",
                    })
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.workerByName}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                workerByName: e.target.value,
              })
            );
          }}
        />
        <CustomSelect
          placeholder={t("company", { ns: "common" })}
          formStyleProps={{
            width: "25%",
            "& .MuiInputBase-input": {
              textTransform: "capitalize",
            },
          }}
          icon={<ApartmentIcon />}
          value={searchKeys.workerByCompanyId}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                workerByCompanyId: e.target.value,
              })
            );
          }}
        >
          <MenuItem value={"All"}>{t("all", { ns: "common" })}</MenuItem>
          {allCompanies?.map((x, i) => (
            <MenuItem key={i} value={x._id}>
              {x.name}
            </MenuItem>
          ))}
        </CustomSelect>
      </Stack>
    );
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("workersLists")}
        subtitle={t("manageWorkers")}
        // headerActions={allWorkers?.length > 0 ? renderHeaderAction() : null}
        searchActions={renderSearchAction()}
      >
        <DynamicPaginatedTable
          showPagination={true}
          minWidth={1200}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allWorkers}
          handleChangeStatus={handleOpenConfirmDialog}
          showActions={true}
        />
        {allWorkers?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}
          </Typography>
        )}
        <StatusBlockDialog
          ref={openBlockStatusDialog}
          onConfirm={handleUpdateUserStatus}
        />
      </PaperContainer>
    </>
  );
}

export default WokerLists;
